import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { compose } from '../../utils/compose';
import { withFirebase } from '../Firebase';
import { AuthUserContext, withAuthorization } from '../Session';

import PersonalDetailsStep from './steps/PersonalDetailsStep';
import TeamInfoStep from './steps/TeamInfoStep';
import TerritoryStep from './steps/TerritoryStep';
import BrandingNeedsStep from './steps/BrandingNeedsStep';
import ReviewStep from './steps/ReviewStep';

import './styles.css';

const STEPS = [
  { id: 'personalDetails', title: 'Personal Details', component: PersonalDetailsStep },
  { id: 'teamMembers', title: 'Team Information', component: TeamInfoStep },
  { id: 'territory', title: 'Territory & Coverage', component: TerritoryStep },
  { id: 'brandingRequests', title: 'Branding Materials', component: BrandingNeedsStep },
  { id: 'review', title: 'Review & Submit', component: ReviewStep },
];

const FranchiseOnboardingBase = ({ firebase }) => {
  const { franchiseId } = useParams();
  const history = useHistory();
  const authUser = useContext(AuthUserContext);
  
  console.log('🚀 FranchiseOnboarding component rendered!');
  console.log('📋 franchiseId from URL:', franchiseId);
  console.log('👤 authUser from useContext:', authUser);
  
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const [error, setError] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    personalDetails: {
      fullName: '',
      phone: '',
      address: {
        street: '',
        city: '',
        state: '',
        pincode: '',
      },
      emergencyContact: '',
      photoURL: '',
    },
    teamMembers: [],
    territory: {
      primaryCity: '',
      coverageAreas: [],
      propertyTypes: [],
    },
    brandingRequests: {
      businessCards: { needed: false, quantity: 0, urgency: 'normal' },
      brochures: { needed: false, quantity: 0 },
      digitalAssets: false,
      socialMediaTemplates: false,
      emailSignature: false,
      propertyListingTemplates: false,
      letterhead: false,
      other: '',
    },
    preferences: {
      language: 'English',
      specialRequirements: '',
    },
    review: {
      acceptedTerms: false,
    },
  });

  const loadOnboardingData = useCallback(async () => {
    console.log('🔍 Starting loadOnboardingData...');
    console.log('authUser:', authUser);
    console.log('franchiseId:', franchiseId);
    
    try {
      setLoading(true);
      
      // Ensure authUser is available
      if (!authUser || !authUser.uid) {
        console.error('❌ No authUser or uid');
        setError('User not authenticated');
        setLoading(false);
        return;
      }
      
      console.log('✅ AuthUser validated:', authUser.uid);
      
      // Check if franchise exists and belongs to user
      console.log('📦 Fetching franchise doc...');
      const franchiseDoc = await firebase.franchiseAPI.franchise(franchiseId).get();
      console.log('📦 Franchise doc exists?', franchiseDoc.exists);
      
      if (!franchiseDoc.exists) {
        console.error('❌ Franchise not found');
        setError('Franchise not found');
        setLoading(false);
        return;
      }
      
      const franchiseData = franchiseDoc.data();
      console.log('✅ Franchise data:', franchiseData);
      
      if (franchiseData.ownerId !== authUser.uid) {
        console.error('❌ Permission denied');
        setError('You do not have permission to access this franchise');
        setLoading(false);
        return;
      }
      
      // Load existing onboarding data if any
      console.log('📋 Fetching onboarding doc...');
      const onboardingDoc = await firebase.franchiseAPI.franchiseOnboarding(franchiseId).get();
      console.log('📋 Onboarding doc exists?', onboardingDoc.exists);
      
      if (onboardingDoc.exists) {
        const onboardingData = onboardingDoc.data();
        console.log('✅ Onboarding data:', onboardingData);
        
        // Merge existing data
        if (onboardingData.personalDetails) {
          setFormData(prev => ({ ...prev, personalDetails: onboardingData.personalDetails }));
        }
        if (onboardingData.teamMembers) {
          setFormData(prev => ({ ...prev, teamMembers: onboardingData.teamMembers }));
        }
        if (onboardingData.territory) {
          setFormData(prev => ({ ...prev, territory: onboardingData.territory }));
        }
        if (onboardingData.brandingRequests) {
          setFormData(prev => ({ ...prev, brandingRequests: onboardingData.brandingRequests }));
        }
        if (onboardingData.preferences) {
          setFormData(prev => ({ ...prev, preferences: onboardingData.preferences }));
        }
        
        // Allow users to edit even after completion - no redirect
        console.log('✅ Loaded onboarding data, user can continue editing');
      }
      
      console.log('✅ Loading complete!');
      setLoading(false);
      setDataLoaded(true);
    } catch (err) {
      console.error('❌ Error loading onboarding data:', err);
      setError('Failed to load onboarding data: ' + err.message);
      setLoading(false);
    }
  }, [authUser, franchiseId, firebase, history]);

  useEffect(() => {
    console.log('🔄 useEffect triggered!');
    console.log('  - authUser exists?', !!authUser);
    console.log('  - franchiseId exists?', !!franchiseId);
    console.log('  - dataLoaded?', dataLoaded);
    
    if (authUser && franchiseId && !dataLoaded) {
      console.log('✅ All conditions met, calling loadOnboardingData...');
      loadOnboardingData();
    } else {
      console.log('⏸️ Conditions not met, skipping load');
    }
  }, [authUser, franchiseId, dataLoaded, loadOnboardingData]);

  // Auto-scroll to top when step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  // Auto-save functionality - saves data 2 seconds after last change
  useEffect(() => {
    if (!dataLoaded || !franchiseId) return;

    const autoSaveTimer = setTimeout(async () => {
      const stepKey = STEPS[currentStep].id;
      if (stepKey === 'review') return; // Don't auto-save review step

      try {
        setAutoSaving(true);
        await firebase.franchiseAPI.updateOnboardingStep(
          franchiseId,
          stepKey,
          formData[stepKey],
          authUser?.uid // Pass userId to save in onboarding doc
        );
        console.log(`💾 Auto-saved ${stepKey} data for user ${authUser?.uid}`);
      } catch (err) {
        console.error('Auto-save error:', err);
      } finally {
        setAutoSaving(false);
      }
    }, 2000); // Debounce for 2 seconds

    return () => clearTimeout(autoSaveTimer);
  }, [formData, currentStep, franchiseId, dataLoaded, firebase]);

  // Validation functions for each step
  const validateStep = (stepId) => {
    const data = formData[stepId];
    
    switch (stepId) {
      case 'personalDetails':
        if (!data.fullName || !data.phone) {
          return 'Please fill in all required fields before moving forward!';
        }
        if (!/^[6-9]\d{9}$/.test(data.phone.replace(/\D/g, ''))) {
          return 'Please enter a valid 10-digit Indian phone number';
        }
        if (!data.address?.city || !data.address?.state || !data.address?.pincode) {
          return 'Please complete your address details';
        }
        if (!/^\d{6}$/.test(data.address.pincode)) {
          return 'Please enter a valid 6-digit pincode';
        }
        return null;
        
      case 'teamMembers':
        // Team members are optional, but if added, they must be valid
        if (data && data.length > 0) {
          for (let member of data) {
            if (!member.name || !member.email || !member.preferredEmail) {
              return 'All team members must have Name, Email, and Preferred Email';
            }
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(member.email)) {
              return 'Please enter valid email addresses for all team members';
            }
            if (!/^[a-z0-9._-]+$/.test(member.preferredEmail)) {
              return 'Preferred email handles can only contain lowercase letters, numbers, dots, hyphens, and underscores';
            }
          }
        }
        return null;
        
      case 'territory':
        if (!data.primaryCity) {
          return 'Please select your primary city';
        }
        if (!data.coverageAreas || data.coverageAreas.length === 0) {
          return 'Please select at least one coverage area';
        }
        if (!data.propertyTypes || data.propertyTypes.length === 0) {
          return 'Please select at least one property type';
        }
        return null;
        
      case 'brandingRequests':
        // Branding requests are optional
        return null;
        
      case 'review':
        if (!data.acceptedTerms) {
          return 'Please accept the Terms & Conditions to continue';
        }
        return null;
        
      default:
        return null;
    }
  };

  const handleStepChange = (stepIndex) => {
    if (stepIndex >= 0 && stepIndex < STEPS.length) {
      setCurrentStep(stepIndex);
    }
  };

  const handleNext = async () => {
    try {
      setSaving(true);
      setError(null);
      
      // Validate current step before proceeding
      const validationError = validateStep(STEPS[currentStep].id);
      if (validationError) {
        setError(validationError);
        setSaving(false);
        return;
      }
      
      // Save current step data
      const stepKey = STEPS[currentStep].id;
      if (stepKey !== 'review') {
        await firebase.franchiseAPI.updateOnboardingStep(
          franchiseId,
          stepKey,
          formData[stepKey]
        );
      }
      
      // Move to next step
      if (currentStep < STEPS.length - 1) {
        setCurrentStep(currentStep + 1);
      }
      
      setSaving(false);
    } catch (err) {
      console.error('Error saving step:', err);
      setError('Failed to save data. Please try again.');
      setSaving(false);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setError(null); // Clear any errors when going back
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      setError(null);
      
      // Validate terms acceptance
      const validationError = validateStep(STEPS[currentStep].id);
      if (validationError) {
        setError(validationError);
        setSaving(false);
        return;
      }
      
      // Calculate email costs
      const teamMembersCount = formData.teamMembers?.length || 0;
      const additionalEmails = Math.max(0, teamMembersCount - 1);
      const additionalCost = additionalEmails * 2500;
      
      // Prepare submission data
      const submissionData = {
        ...formData,
        emailCosts: {
          totalEmails: teamMembersCount,
          includedEmails: 1,
          additionalEmails,
          additionalCost
        },
        submittedAt: firebase.fieldValue.serverTimestamp(),
        status: 'pending_setup'
      };
      
      // Submit onboarding
      await firebase.franchiseAPI.submitOnboarding(franchiseId, submissionData);
      
      // Update franchise status
      await firebase.franchiseAPI.franchise(franchiseId).update({
        status: 'pending_setup',
        submittedAt: firebase.fieldValue.serverTimestamp(),
        emailCosts: {
          totalEmails: teamMembersCount,
          includedEmails: 1,
          additionalEmails,
          additionalCost
        }
      });
      
      // Update franchise owner status
      await firebase.franchiseAPI.franchiseOwner(authUser.uid).update({
        onboardingCompleted: true,
        status: 'pending_setup'
      });
      
      // Show success page instead of redirecting
      setSubmitted(true);
      setSaving(false);
    } catch (err) {
      console.error('Error submitting onboarding:', err);
      setError('Failed to submit onboarding. Please try again.');
      setSaving(false);
    }
  };

  const updateStepData = (stepKey, data) => {
    setFormData(prev => ({
      ...prev,
      [stepKey]: data,
    }));
    // Clear error when user starts editing
    if (error) {
      setError(null);
    }
  };

  if (loading) {
    return (
      <div className="onboarding-loading">
        <div className="loader"></div>
        <p>Loading franchise onboarding...</p>
        <div style={{marginTop: '20px', fontSize: '12px', color: '#666'}}>
          <p>Debug Info:</p>
          <p>franchiseId: {franchiseId || 'MISSING'}</p>
          <p>authUser: {authUser ? authUser.uid : 'MISSING'}</p>
          <p>dataLoaded: {dataLoaded.toString()}</p>
          <p>error: {error || 'none'}</p>
        </div>
      </div>
    );
  }

  // Show success page after submission
  if (submitted) {
    return (
      <div className="onboarding-success" style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px',
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)'
      }}>
        <div style={{
          maxWidth: '700px',
          width: '100%',
          background: '#ffffff',
          borderRadius: '16px',
          padding: '48px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          textAlign: 'center'
        }}>
          <div style={{
            width: '100px',
            height: '100px',
            margin: '0 auto 32px',
            background: 'linear-gradient(135deg, var(--auth-gold) 0%, #e4c568 100%)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '48px'
          }}>
            ⏱
          </div>

          <h1 style={{
            fontSize: '32px',
            color: '#2c3e50',
            marginBottom: '16px',
            fontWeight: '600'
          }}>
            Setting Up Your Franchise
          </h1>

          <p style={{
            fontSize: '18px',
            color: '#6c757d',
            marginBottom: '40px',
            lineHeight: '1.6'
          }}>
            Thank you for submitting your franchise application! We're currently setting up everything for you.
          </p>

          <div style={{
            textAlign: 'left',
            background: '#f8f9fa',
            borderRadius: '12px',
            padding: '32px',
            marginBottom: '40px'
          }}>
            <h3 style={{
              fontSize: '20px',
              color: '#2c3e50',
              marginBottom: '24px',
              fontWeight: '600'
            }}>
              What we're preparing for you:
            </h3>
            <ul style={{
              listStyle: 'none',
              padding: 0,
              margin: 0
            }}>
              <li style={{
                padding: '16px 0',
                borderBottom: '1px solid #dee2e6',
                color: '#495057',
                fontSize: '16px'
              }}>
                Creating your @douglasmccartney.com email accounts
              </li>
              <li style={{
                padding: '16px 0',
                borderBottom: '1px solid #dee2e6',
                color: '#495057',
                fontSize: '16px'
              }}>
                Preparing your branding materials
              </li>
              <li style={{
                padding: '16px 0',
                borderBottom: '1px solid #dee2e6',
                color: '#495057',
                fontSize: '16px'
              }}>
                Setting up your franchise portal
              </li>
              <li style={{
                padding: '16px 0',
                color: '#495057',
                fontSize: '16px'
              }}>
                Configuring training resources
              </li>
            </ul>
          </div>

          <div style={{
            background: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '32px',
            textAlign: 'left'
          }}>
            <p style={{
              margin: 0,
              color: '#856404',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              <strong>Expected Setup Time:</strong> Within 24 hours of submission
            </p>
          </div>

          <p style={{
            fontSize: '14px',
            color: '#6c757d',
            marginBottom: '32px'
          }}>
            <strong>You'll receive an email</strong> with your credentials and next steps when everything is ready.
          </p>

          <button
            onClick={() => history.push('/dashboard')}
            style={{
              background: 'linear-gradient(135deg, var(--auth-gold) 0%, #e4c568 100%)',
              color: '#2c3e50',
              border: 'none',
              borderRadius: '8px',
              padding: '16px 48px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 4px 16px rgba(212, 175, 55, 0.3)',
              transition: 'transform 0.2s ease'
            }}
            onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
            onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const CurrentStepComponent = STEPS[currentStep].component;

  return (
    <div className="franchise-onboarding">
      <div className="onboarding-header">
        <div className="header-logo">
          <div className="logo-icon">D&M</div>
          <h1>Franchise Onboarding</h1>
        </div>
        <p className="header-subtitle">Complete your franchise setup</p>
      </div>

      {/* Progress Indicator */}
      <div className="progress-indicator">
        {STEPS.map((step, index) => (
          <div
            key={step.id}
            className={`progress-step ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
          >
            <div className="step-number">
              {index < currentStep ? '✓' : index + 1}
            </div>
            <div className="step-title">{step.title}</div>
            {index < STEPS.length - 1 && <div className="step-line"></div>}
          </div>
        ))}
      </div>

      {/* Current Step Content */}
      <div className="onboarding-content">
        <CurrentStepComponent
          data={formData[STEPS[currentStep].id] || {}}
          allData={formData}
          onChange={(data) => updateStepData(STEPS[currentStep].id, data)}
        />
      </div>

      {/* Navigation Buttons */}
      <div className="onboarding-navigation">
        {/* Validation Message */}
        {error && (
          <div style={{
            gridColumn: '1 / -1',
            background: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            color: '#856404'
          }}>
            <span style={{ fontSize: '20px' }}>⚠️</span>
            <span style={{ fontSize: '14px', fontWeight: '500' }}>{error}</span>
          </div>
        )}

        {autoSaving && (
          <div style={{
            gridColumn: '1 / -1',
            padding: '8px 16px',
            background: 'rgba(100, 149, 237, 0.1)',
            border: '1px solid rgba(100, 149, 237, 0.3)',
            borderRadius: '6px',
            marginBottom: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: '#4169E1',
            fontSize: '13px',
            fontWeight: '500'
          }}>
            <div style={{
              width: '12px',
              height: '12px',
              border: '2px solid rgba(65, 105, 225, 0.3)',
              borderTopColor: '#4169E1',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            💾 Auto-saving your progress...
          </div>
        )}

        <button
          className="btn-back"
          onClick={handleBack}
          disabled={currentStep === 0 || saving}
        >
          ← Back
        </button>
        
        <div className="nav-info">
          Step {currentStep + 1} of {STEPS.length}
        </div>

        {currentStep < STEPS.length - 1 ? (
          <button
            className="btn-next"
            onClick={handleNext}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Next →'}
          </button>
        ) : (
          <button
            className="btn-submit"
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? 'Submitting...' : 'Submit Application →'}
          </button>
        )}
      </div>
    </div>
  );
};

const condition = authUser => !!authUser;

const FranchiseOnboarding = compose(
  withAuthorization(condition),
  withFirebase,
)(FranchiseOnboardingBase);

export default FranchiseOnboarding;

